import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { AlertTriangle, Trash2, Check, X, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from './alert';

/**
 * ConfirmDialog - A reusable confirmation dialog for destructive or important actions
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onOpenChange - Callback when open state changes
 * @param {string} title - Dialog title
 * @param {string} description - Dialog description
 * @param {string} variant - 'danger' | 'warning' | 'info' (default: 'warning')
 * @param {string} confirmText - Text for confirm button (default: 'Confirm')
 * @param {string} cancelText - Text for cancel button (default: 'Cancel')
 * @param {function} onConfirm - Callback when confirmed
 * @param {function} onCancel - Callback when cancelled
 * @param {boolean} loading - Whether the confirm action is loading
 * @param {React.ReactNode} children - Additional content to render in the dialog
 * @param {string} alertMessage - Optional alert message to display
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  variant = 'warning',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  children,
  alertMessage,
}) => {
  const variantConfig = {
    danger: {
      icon: Trash2,
      iconClass: 'text-red-600',
      titleClass: 'text-red-700',
      buttonClass: 'bg-red-600 hover:bg-red-700',
      alertClass: 'border-red-300 bg-red-50',
      alertTextClass: 'text-red-700',
    },
    warning: {
      icon: AlertTriangle,
      iconClass: 'text-amber-600',
      titleClass: 'text-amber-700',
      buttonClass: 'bg-amber-600 hover:bg-amber-700',
      alertClass: 'border-amber-300 bg-amber-50',
      alertTextClass: 'text-amber-700',
    },
    info: {
      icon: Check,
      iconClass: 'text-blue-600',
      titleClass: 'text-blue-700',
      buttonClass: 'bg-blue-600 hover:bg-blue-700',
      alertClass: 'border-blue-300 bg-blue-50',
      alertTextClass: 'text-blue-700',
    },
  };

  const config = variantConfig[variant] || variantConfig.warning;
  const Icon = config.icon;

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    await onConfirm?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${config.titleClass}`}>
            <Icon className={`h-5 w-5 ${config.iconClass}`} />
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-slate-600">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {alertMessage && (
          <Alert className={config.alertClass}>
            <Icon className={`h-4 w-4 ${config.iconClass}`} />
            <AlertDescription className={config.alertTextClass}>
              {alertMessage}
            </AlertDescription>
          </Alert>
        )}

        {children}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            data-testid="confirm-dialog-cancel-btn"
          >
            <X className="h-4 w-4 mr-2" />
            {cancelText}
          </Button>
          <Button
            className={config.buttonClass}
            onClick={handleConfirm}
            disabled={loading}
            data-testid="confirm-dialog-confirm-btn"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * DeleteConfirmDialog - A specialized confirm dialog for delete actions
 */
export const DeleteConfirmDialog = ({
  open,
  onOpenChange,
  title = 'Delete Confirmation',
  description = 'Are you sure you want to delete this item? This action cannot be undone.',
  itemName,
  onConfirm,
  onCancel,
  loading = false,
  children,
}) => {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={itemName ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.` : description}
      variant="danger"
      confirmText="Delete"
      cancelText="Cancel"
      onConfirm={onConfirm}
      onCancel={onCancel}
      loading={loading}
    >
      {children}
    </ConfirmDialog>
  );
};

export default ConfirmDialog;
